import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * A GUID: shortened, linked, copyable.
 *
 * Every list in this admin is full of them, and they are unreadable at full
 * length and useless truncated — you need the whole thing to paste into a query
 * or a Slack message. So the prototype shows the first segment and puts a copy
 * button next to it, with the full value in the title attribute.
 *
 * Two ways to link, and the difference matters. `to` is a route inside this
 * tree and renders a `Link`, so the navigation stays client-side. `href` points
 * at the old admin and renders a plain anchor, so the jump is a full page load
 * that leaves this tree's styles behind. As detail pages get rebuilt, callers
 * move from `href` to `to`.
 */
export default function Guid({ value, href, to, short = true }) {
  const [copied, setCopied] = useState(false);

  if (!value) {
    return <span className="next-muted">—</span>;
  }

  const label = short ? String(value).split('-')[0] : value;

  const copy = async event => {
    event.preventDefault();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (error) {
      // Clipboard needs a permission that may be refused; the title attribute
      // still holds the full value for a manual select.
    }
  };

  return (
    <span className="next-guid">
      {to ? (
        <Link className="next-guid__link" to={to} title={value}>{label}</Link>
      ) : href ? (
        <a className="next-guid__link" href={href} title={value}>{label}</a>
      ) : (
        <span className="next-guid__link" title={value}>{label}</span>
      )}
      <button
        type="button"
        className="next-guid__copy"
        onClick={copy}
        title="Copy full id"
        aria-label={copied ? 'Copied' : 'Copy full id'}
      >
        {copied ? '✓' : '⧉'}
      </button>
    </span>
  );
}

Guid.propTypes = {
  value: PropTypes.string,
  href: PropTypes.string,
  to: PropTypes.string,
  short: PropTypes.bool
};
